import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useDashboard } from '../DashboardContext';
import { formatEventDate } from '../normalizeEventDate';

interface Props { compareMode: boolean; prevPeriodLabel: string; }

const PERIOD_LABELS: Record<string, string> = {
    monthly:    'This Month',
    quarterly:  'This Quarter',
    halfyearly: 'This Half-Year',
    annual:     'This Year',
};

const CampaignActivity: React.FC<Props> = ({ compareMode, prevPeriodLabel }) => {
    const { emailCampaigns, whatsappStats, loading, timeframe } = useDashboard();
    const [showAllModal, setShowAllModal] = useState(false);
    const [showAllBroadcasts, setShowAllBroadcasts] = useState(false);

    const periodLabel = PERIOD_LABELS[timeframe] ?? 'This Period';

    const Skeleton = () => (
        <div className="placeholder-glow p-3">
            {[1, 2, 3].map(i => (
                <div key={i} className="placeholder col-12 mb-2 d-block" style={{ height: 32 }}></div>
            ))}
        </div>
    );

    // Show "View all" link only when there are more campaigns than the 10 shown
    const hasMoreCampaigns = emailCampaigns.length > 10;
    // Show "View all broadcasts" link if total broadcasts exceed what we show (3)
    const hasMoreBroadcasts = (whatsappStats?.totalBroadcasts ?? 0) > 3;

    // Shared email-campaign table renderer (used by the card + the "View all" modal)
    const renderEmailTable = (rows: any[]) => (
        <div className="table-responsive">
            <table className="table table-sm mb-0">
                <thead className="table-light">
                    <tr>
                        <th className="font-12">Campaign</th>
                        <th className="font-12">Sent</th>
                        <th className="font-12">Delivered</th>
                        <th className="font-12">Open</th>
                        <th className="font-12">Click</th>
                        {compareMode && <th className="font-12">▲ vs Prior</th>}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((c: any) => (
                        <tr key={c.id}>
                            <td>
                                <div className="fw-semibold font-13">{c.name}</div>
                                <div className="text-muted" style={{ fontSize: 11 }}>
                                    {c.date ? formatEventDate(c.date) : ''}
                                    {c.category ? ` · ${c.category}` : ''}
                                </div>
                            </td>
                            <td className="font-13">{(c.sent || 0).toLocaleString()}</td>
                            <td className="font-13">{(c.delivered || 0).toLocaleString()}</td>
                            <td><span className="fw-bold text-primary font-13">{c.openRate}%</span></td>
                            <td><span className="fw-bold text-primary font-13">{c.clickRate}%</span></td>
                            {compareMode && <td className="text-muted small">—</td>}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    // Shared WhatsApp-broadcast table renderer (used by the "View all" modal)
    const renderBroadcastTable = (rows: any[]) => (
        <div className="table-responsive">
            <table className="table table-sm mb-0">
                <thead className="table-light">
                    <tr>
                        <th className="font-12">Broadcast</th>
                        <th className="font-12">Delivered</th>
                        <th className="font-12">Read</th>
                        <th className="font-12">Replied</th>
                        <th className="font-12">Reply Rate</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((b: any, i: number) => (
                        <tr key={i}>
                            <td>
                                <div className="fw-semibold font-13">{b.name}</div>
                                {b.date && (
                                    <div className="text-muted" style={{ fontSize: 11 }}>{formatEventDate(b.date)}</div>
                                )}
                            </td>
                            <td className="font-13">{(b.delivered || 0).toLocaleString()}</td>
                            <td><span className="fw-bold text-success font-13">{b.readRate}%</span></td>
                            <td className="font-13">{(b.replied || 0).toLocaleString()}</td>
                            <td><span className="fw-bold text-success font-13">{b.replyRate}%</span></td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );

    return (
        <div className="mb-4">
            <h5 id="section-campaigns" className="font-16 font-600 mb-3">📧 Campaign Interactions</h5>
            <div className="row g-3">

                {/* ── Email Campaign Interactions Table (col 7) ── */}
                <div className="col-lg-7">
                    <div className="card h-100">
                        <div className="card-header bg-white">
                            <div className="fw-semibold font-14">📧 Email Campaign Interactions</div>
                            {/* Period label · N campaigns */}
                            <div className="text-muted small">
                                {periodLabel} · {emailCampaigns.length} campaign{emailCampaigns.length !== 1 ? 's' : ''}
                            </div>
                        </div>
                        <div className="card-body p-0">
                            {loading ? <Skeleton /> : emailCampaigns.length === 0 ? (
                                <div className="text-center py-4 text-muted small">
                                    No campaigns sent in this period
                                </div>
                            ) : (
                                <>
                                    {renderEmailTable(emailCampaigns.slice(0, 10))}
                                    {/* View all → opens popup with the full list */}
                                    {hasMoreCampaigns && (
                                        <div className="text-center py-2 border-top">
                                            <button
                                                className="btn btn-link btn-sm text-primary p-0"
                                                style={{ fontSize: 13 }}
                                                onClick={() => setShowAllModal(true)}
                                            >
                                                View all {emailCampaigns.length} campaigns →
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {/* ── WhatsApp Campaign Activity (col 5) ── */}
                <div className="col-lg-5">
                    <div className="card h-100">
                        <div className="card-header bg-white">
                            <div className="fw-semibold font-14">💬 WhatsApp Campaign Activity</div>
                            {/* Period label · N broadcasts */}
                            <div className="text-muted small">
                                {periodLabel} · {whatsappStats?.totalBroadcasts ?? 0} broadcast{whatsappStats?.totalBroadcasts !== 1 ? 's' : ''}
                            </div>
                        </div>
                        <div className="card-body">
                            {loading ? <Skeleton /> : !whatsappStats || whatsappStats.totalBroadcasts === 0 ? (
                                <div className="text-center py-4 text-muted small">
                                    No WhatsApp campaigns in this period
                                </div>
                            ) : (
                                <>
                                    {/* 4 stat tiles in 2×2 grid */}
                                    <div className="row g-2 mb-3">
                                        {[
                                            { label: 'Delivered', value: whatsappStats.delivered },
                                            { label: 'Read Rate', value: `${whatsappStats.readRate}%` },
                                            { label: 'Replied',   value: whatsappStats.replied },
                                            { label: 'Reply Rate', value: `${whatsappStats.replyRate}%` },
                                        ].map((s, i) => (
                                            <div key={i} className="col-6">
                                                <div className="bg-light rounded p-2 text-center">
                                                    <div className="fw-bold font-16">{s.value}</div>
                                                    <div className="text-muted small">{s.label}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>

                                    {/* Compare row — visible only in Compare mode */}
                                    {compareMode && (
                                        <div className="bg-light rounded p-2 mb-3 small text-muted">
                                            <span className="fw-bold">{prevPeriodLabel}:</span> comparison data loading…
                                        </div>
                                    )}

                                    {/* Per-broadcast progress bars (max 3) */}
                                    {whatsappStats.broadcasts?.slice(0, 3).map((b: any, i: number) => (
                                        <div key={i} className="mb-2">
                                            <div className="d-flex justify-content-between small mb-1">
                                                <span className="text-truncate" style={{ maxWidth: '75%' }}>{b.name}</span>
                                                <span className="fw-bold">{b.readRate}%</span>
                                            </div>
                                            <div className="progress" style={{ height: 6 }}>
                                                <div className="progress-bar bg-success"
                                                    style={{ width: `${b.readRate}%` }}>
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* View all broadcasts → opens popup with the full list */}
                                    {hasMoreBroadcasts && (
                                        <div className="text-center mt-2">
                                            <button
                                                className="btn btn-link btn-sm text-primary p-0"
                                                style={{ fontSize: 13 }}
                                                onClick={() => setShowAllBroadcasts(true)}
                                            >
                                                View all {whatsappStats.totalBroadcasts} broadcasts →
                                            </button>
                                        </div>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── "View all campaigns" popup ── */}
            <Modal show={showAllModal} onHide={() => setShowAllModal(false)} size="lg" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title className="font-16">
                        📧 Email Campaign Interactions
                        <div className="text-muted small fw-normal">
                            {periodLabel} · {emailCampaigns.length} campaign{emailCampaigns.length !== 1 ? 's' : ''}
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {renderEmailTable(emailCampaigns)}
                </Modal.Body>
            </Modal>

            {/* ── "View all broadcasts" popup ── */}
            <Modal show={showAllBroadcasts} onHide={() => setShowAllBroadcasts(false)} size="lg" centered scrollable>
                <Modal.Header closeButton>
                    <Modal.Title className="font-16">
                        💬 WhatsApp Campaign Activity
                        <div className="text-muted small fw-normal">
                            {periodLabel} · {whatsappStats?.totalBroadcasts ?? 0} broadcast{whatsappStats?.totalBroadcasts !== 1 ? 's' : ''}
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-0">
                    {renderBroadcastTable(whatsappStats?.broadcasts || [])}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default CampaignActivity;
